import { useCallback } from 'react';

import { SharedCheckoutButtonsProps } from '#components/molecules/Cart/buttons/CheckoutButtons/CheckoutButtons.types';
import { useShopProvider } from '#provider/useShopProvider.js';
import { useCreateCart, useShowCart, useUpdateCart } from '@givebacks/api/store';
import { useFormyValidator } from '@givebacks/formy';
import { AxiosError } from 'core';
import { getErrorMessage_v2 } from 'core/Utils';
import { notifications } from 'ui';

import { useShopStorage } from './useShopStorage';

interface UseCartUpdateArgs extends SharedCheckoutButtonsProps {
  hasOptions?: boolean;
}
const useUpdateOrCreateCart = () => {
  const { mutate: createCart, isPending: isCreateLoading } = useCreateCart({
    mutationKey: ['cart', 'create'],
  });
  const { mutate: updateCart, isPending: isUpdateLoading } = useUpdateCart({
    mutationKey: ['cart', 'update'],
  });

  return {
    createCart,
    updateCart,
    isLoading: isCreateLoading || isUpdateLoading,
  };
};

export const useCartUpdateHandler = ({
  storeId,
  storeItemId,
  cartFields = {},
  cartItems,
  quantity,
  validateOnClick,
  onOptions,
  onCartCreate,
  hasOptions,
  scopeId,
  onSuccess,
}: UseCartUpdateArgs) => {
  const { createCart, updateCart, isLoading } = useUpdateOrCreateCart();
  const { getCartIdByStoreId, updateOrAddCart, removeCart } = useShopStorage();
  const { provider } = useShopProvider();
  const cartId = getCartIdByStoreId(storeId);

  const { isSuccess } = useShowCart({
    cartId: cartId,
    causeId: storeId,
  });

  const { validateForms } = useFormyValidator();

  const handleCartUpdate = useCallback(() => {
    if (onOptions && hasOptions) {
      if (validateOnClick) {
        const isValid = validateForms().isValid;
        if (!isValid) return;
      }
      return onOptions({ quantity, checkoutCartAction: 'add', cartFields, cartItems });
    }

    if (validateOnClick) {
      const isValid = validateForms().isValid;
      if (!isValid) return;
    }

    if (isSuccess && cartId) {
      updateCart(
        {
          cart: {
            cart_items: cartItems,
            ...cartFields,
          },
          uuid: cartId,
          causeId: storeId,
          scopeId,
        },
        {
          onSuccess(data) {
            updateOrAddCart({
              cartId: data.uuid,
              cart: data,
              storeId: storeId,
              emit: true,
            });
            onCartCreate?.(data);
            onSuccess?.();
          },
          onError(e) {
            const error = e as AxiosError;

            notifications.show({
              message: getErrorMessage_v2(error, 'Error adding item to cart'),
              variant: 'error',
              autoClose: 20 * 1000,
            });

            if ('response' in error && error.response?.status === 404) {
              removeCart(cartId);
            }
          },
        }
      );
    } else {
      createCart(
        { storeItemId, causeId: storeId, cart_items: cartItems, provider, ...cartFields },
        {
          onSuccess(data, variables) {
            updateOrAddCart({
              emit: true,
              cartId: data.uuid,
              storeId: variables.causeId,
              cart: data,
            });
            onCartCreate?.(data);
            onSuccess?.();
          },
          onError(e) {
            const error = e as Error | AxiosError;
            notifications.show({
              message: getErrorMessage_v2(error),
              variant: 'error',
              autoClose: 20 * 1000,
            });
          },
        }
      );
    }
  }, [
    cartFields,
    cartId,
    cartItems,
    createCart,
    validateForms,
    hasOptions,
    isSuccess,
    onCartCreate,
    onOptions,
    onSuccess,
    provider,
    quantity,
    removeCart,
    scopeId,
    storeId,
    storeItemId,
    updateCart,
    updateOrAddCart,
    validateOnClick,
  ]);

  return {
    cartId,
    handleCartUpdate,
    isLoading: isLoading,
  };
};
